import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  CircularProgress,
  LinearProgress,
  Checkbox,
  Select,
  MenuItem,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { listFiles, uploadFile, getDownloadUrl, getViewUrl, deleteFile, updateFileExpiry } from '../api';

const TEXT_EXTENSIONS = new Set([
  'txt', 'md', 'csv', 'json', 'xml', 'yaml', 'yml', 'toml', 'ini', 'cfg',
  'log', 'html', 'htm', 'css', 'js', 'ts', 'jsx', 'tsx', 'py', 'java',
  'rb', 'go', 'rs', 'c', 'cpp', 'h', 'hpp', 'sh', 'bash', 'zsh',
  'sql', 'graphql', 'env', 'gitignore', 'dockerfile', 'makefile',
  'properties', 'conf', 'svg', 'tex', 'rst', 'adoc',
]);

const LARGE_FILE_THRESHOLD = 5 * 1024 * 1024; // 5 MB

const isTextFile = (filename) => {
  const ext = filename.split('.').pop().toLowerCase();
  return TEXT_EXTENSIONS.has(ext);
};

const EXPIRY_OPTIONS = [
  { value: '1month', label: '1 month' },
  { value: '3months', label: '3 months' },
  { value: '1year', label: '1 year' },
  { value: 'never', label: 'Never' },
];

const formatExpiry = (expiresAt) => {
  if (!expiresAt) return 'Never';
  const now = new Date();
  const expires = new Date(expiresAt);
  const diffMs = expires - now;
  if (diffMs < 0) return 'Expired';
  const days = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  if (days <= 1) return 'in 1 day';
  if (days < 30) return `in ${days} days`;
  const months = Math.round(days / 30);
  if (months <= 1) return 'in 1 month';
  if (months < 12) return `in ${months} months`;
  const years = Math.round(days / 365);
  return years <= 1 ? 'in 1 year' : `in ${years} years`;
};

const Files = () => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nextKey, setNextKey] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  const [selected, setSelected] = useState(new Set());

  const fetchFiles = async (reset = false) => {
    setLoading(true);
    try {
      const data = await listFiles(20, reset ? null : nextKey);
      setFiles(reset ? data.files : [...files, ...data.files]);
      setNextKey(data.nextKey);
    } catch (err) {
      console.error('Failed to fetch files:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFiles(true);
  }, []);

  const handleUpload = async (fileList) => {
    if (!fileList || fileList.length === 0) return;

    setUploading(true);
    setUploadProgress(0);

    try {
      const filesArray = Array.from(fileList);
      let completed = 0;
      await Promise.all(filesArray.map(async (file) => {
        await uploadFile(file);
        completed++;
        setUploadProgress((completed / filesArray.length) * 100);
      }));
      fetchFiles(true);
    } catch (err) {
      console.error('Failed to upload file:', err);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleFileInput = (e) => {
    handleUpload(e.target.files);
    e.target.value = '';
  };

  const handleDownload = async (id) => {
    try {
      const { downloadUrl } = await getDownloadUrl(id);
      window.open(downloadUrl, '_blank');
    } catch (err) {
      console.error('Failed to get download URL:', err);
    }
  };

  const handleView = async (file) => {
    if (file.size > LARGE_FILE_THRESHOLD) {
      const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
      if (!window.confirm(`This file is ${sizeMB} MB. Open it in a new tab anyway?`)) return;
    }
    try {
      const { downloadUrl } = await getViewUrl(file.id);
      window.open(downloadUrl, '_blank');
    } catch (err) {
      console.error('Failed to get view URL:', err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this file?')) return;

    try {
      await deleteFile(id);
      setFiles(files.filter((f) => f.id !== id));
    } catch (err) {
      console.error('Failed to delete file:', err);
    }
  };

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    handleUpload(e.dataTransfer.files);
  }, []);

  const handleBulkDelete = async () => {
    if (!window.confirm(`Delete ${selected.size} file(s)?`)) return;
    try {
      await Promise.all([...selected].map((id) => deleteFile(id)));
      setFiles(files.filter((f) => !selected.has(f.id)));
      setSelected(new Set());
    } catch (err) {
      console.error('Failed to bulk delete files:', err);
    }
  };

  const handleExpiryChange = async (id, expiry) => {
    try {
      await updateFileExpiry(id, expiry);
      setFiles(files.map((f) => {
        if (f.id !== id) return f;
        const expiresAt = expiry === 'never' ? null : (() => {
          const days = { '1month': 30, '3months': 90, '1year': 365 }[expiry];
          return new Date(Date.now() + days * 86400000).toISOString();
        })();
        return { ...f, expiresAt };
      }));
    } catch (err) {
      console.error('Failed to update expiry:', err);
    }
  };

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === files.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(files.map((f) => f.id)));
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const formatSize = (bytes) => {
    if (!bytes) return '-';
    const units = ['B', 'KB', 'MB', 'GB'];
    let unitIndex = 0;
    let size = bytes;
    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }
    return `${size.toFixed(1)} ${units[unitIndex]}`;
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Files</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {selected.size > 0 && (
            <Button variant="outlined" color="error" onClick={handleBulkDelete}>
              Delete ({selected.size})
            </Button>
          )}
          <Button
            variant="contained"
            component="label"
            startIcon={<CloudUploadIcon />}
            disabled={uploading}
          >
            Upload
            <input type="file" hidden multiple onChange={handleFileInput} />
          </Button>
        </Box>
      </Box>

      {/* Drop Zone */}
      <Paper
        sx={{
          p: 4,
          mb: 3,
          textAlign: 'center',
          border: '2px dashed',
          borderColor: dragOver ? 'primary.main' : 'grey.300',
          bgcolor: dragOver ? 'action.hover' : 'background.paper',
          cursor: 'pointer',
        }}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => document.querySelector('input[type="file"]').click()}
      >
        <CloudUploadIcon sx={{ fontSize: 48, color: 'grey.400', mb: 1 }} />
        <Typography color="textSecondary">
          Drag and drop files here, or click to select
        </Typography>
      </Paper>

      {uploading && (
        <Box sx={{ mb: 2 }}>
          <LinearProgress variant="determinate" value={uploadProgress} />
          <Typography variant="body2" align="center" sx={{ mt: 1 }}>
            Uploading... {Math.round(uploadProgress)}%
          </Typography>
        </Box>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={selected.size > 0 && selected.size < files.length}
                  checked={files.length > 0 && selected.size === files.length}
                  onChange={toggleSelectAll}
                />
              </TableCell>
              <TableCell>Filename</TableCell>
              <TableCell>Size</TableCell>
              <TableCell>Uploaded</TableCell>
              <TableCell>Expires</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {files.map((file) => (
              <TableRow key={file.id} hover selected={selected.has(file.id)}>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selected.has(file.id)}
                    onChange={() => toggleSelect(file.id)}
                  />
                </TableCell>
                <TableCell>{file.filename}</TableCell>
                <TableCell>{formatSize(file.size)}</TableCell>
                <TableCell>{formatDate(file.createdAt)}</TableCell>
                <TableCell>
                  <Select
                    size="small"
                    value=""
                    displayEmpty
                    renderValue={() => formatExpiry(file.expiresAt)}
                    onChange={(e) => handleExpiryChange(file.id, e.target.value)}
                    sx={{ minWidth: 120 }}
                  >
                    {EXPIRY_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </Select>
                </TableCell>
                <TableCell align="right">
                  {isTextFile(file.filename) && (
                    <IconButton onClick={() => handleView(file)} size="small" title="Open in new tab">
                      <OpenInNewIcon />
                    </IconButton>
                  )}
                  <IconButton onClick={() => handleDownload(file.id)} size="small" title="Download">
                    <DownloadIcon />
                  </IconButton>
                  <IconButton
                    onClick={() => handleDelete(file.id)}
                    size="small"
                    title="Delete"
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {files.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  No files yet
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <CircularProgress />
        </Box>
      )}

      {nextKey && !loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Button onClick={() => fetchFiles()}>Load More</Button>
        </Box>
      )}
    </Box>
  );
};

export default Files;
